/**
 * Custom Library Processor for Export Templates
 * Processes glossary items using customizable export templates
 */

import {
  ProcessedLibraryItem,
  ProcessedLibraryField,
  LibraryItemsByBlueprint,
  processLibraryItem,
  groupItemsByBlueprint,
  generateItemDataJson,
  sanitizeFilename
} from './libraryProcessorShared';
import {
  ExportTemplate,
  TemplateRenderContext,
  TemplateField,
  RenderedTemplate,
  NamingConvention
} from '../../types/exportTemplates';
import {
  renderExportTemplate,
  mapBlueprintField,
  renderDatabaseTemplate,
  renderFolderBasedDatabaseTemplate,
  renderTranslationKeysFile,
  renderTypesFile,
  renderTagsFile,
  renderDialogDatabaseFile,
  renderDialogVariablesFile,
  renderVariablesFile,
  renderRuntimeVariablesFile,
  renderRuntimeLayerBundle,
  renderJsonDialogsFile,
  renderJsonTranslationsFile,
  renderJsonTypesFile,
  renderJsonTagsFile,
  renderJsonVariablesFile,
  renderJsonDialogVariablesFile
} from './templateEngine';
import JSZip from 'jszip';
import { generateManifestJson } from '../utils/packageManifest';
import { getCachedStringsItems } from '../stores/stringsStore';

/**
 * Convert ProcessedLibraryField to TemplateField
 */
function convertToTemplateField(
  field: ProcessedLibraryField,
  exportTemplate: ExportTemplate
): TemplateField {
  return mapBlueprintField(
    {
      id: field.id,
      label: field.name,
      type: field.type,
      req: field.isRequired,
      trans: field.isTranslatable,
      multiple: field.isMultiple
    },
    field.value,
    exportTemplate
  );
}

/**
 * Generate code files for a single blueprint group using custom template
 */
export function generateCustomCodeForBlueprint(
  blueprintGroup: LibraryItemsByBlueprint,
  exportTemplate: ExportTemplate
): Map<string, { content: string; location: string }> {
  const files = new Map<string, { content: string; location: string }>();
  
  // Create context for type file (blueprint definition)
  const typeContext: TemplateRenderContext = {
    templateName: blueprintGroup.blueprintName,
    fields: blueprintGroup.items[0]?.fields.map(f => convertToTemplateField(f, exportTemplate)) || []
  };
  
  // Render type file
  const typeResult = renderExportTemplate(typeContext, exportTemplate);
  if (typeResult.typeFile) {
    files.set(
      typeResult.typeFile.filename,
      {
        content: typeResult.typeFile.content,
        location: typeResult.typeFile.location
      }
    );
  }
  
  // Generate instance files for each item
  blueprintGroup.items.forEach(item => {
    const instanceContext: TemplateRenderContext = {
      templateName: blueprintGroup.blueprintName,
      itemName: item.name,
      itemId: item.id,
      fields: item.fields.map(f => convertToTemplateField(f, exportTemplate)),
      metadata: {
        blueprintId: item.blueprintId
      }
    };
    
    const instanceResult = renderExportTemplate(instanceContext, exportTemplate);
    if (instanceResult.instanceFile) {
      files.set(
        instanceResult.instanceFile.filename,
        {
          content: instanceResult.instanceFile.content,
          location: instanceResult.instanceFile.location
        }
      );
    }
  });
  
  return files;
}

/**
 * Check if a template is a database export pattern
 */
function isDatabaseTemplate(template: ExportTemplate): boolean {
  return template.id === 'preset-roblox-database' || 
         template.id === 'preset-unity-static-typed' ||
         template.id === 'preset-roblox-static-typed' ||
         template.id === 'preset-unity-runtime-database' ||
         template.id === 'preset-unity-id-records' ||
         template.id === 'preset-json-database' ||
         template.id === 'preset-json-folder-based' ||
         (template.options?.ignoreSections === true && 
          template.categoryMapping && 
          Object.keys(template.categoryMapping).length > 0);
}

/**
 * Check if a template uses folder-based structure (static strongly-typed access or runtime database)
 */
function isFolderBasedTemplate(template: ExportTemplate): boolean {
  return template.id === 'preset-unity-static-typed' ||
         template.id === 'preset-roblox-static-typed' ||
         template.id === 'preset-unity-runtime-database' ||
         template.id === 'preset-unity-id-records' ||
         template.id === 'preset-json-folder-based';
}

/**
 * Create a complete data package using custom export template
 * 
 * @param blueprintGroups - Items grouped by blueprint
 * @param gameId - The game ID
 * @param gameName - The game name for filenames
 * @param exportTemplate - The export template configuration
 * @param blueprints - Available blueprints for field extraction
 * @param types - Types data for resolving references
 * @param tags - Tags data for resolving references
 * @param allItems - All items for resolving glossaryItem references
 * @param folderPathResolver - Optional function to resolve folder ID to path string for folder-based exports
 * @param namingConventionOverride - Optional override for naming conventions (e.g., 'original', 'camelCase')
 * @param customNamespace - Optional custom namespace for C# exports (default: 'Grimoire.Data')
 * @param customClassName - Optional custom class name for the root class/table (default: 'Database')
 * @param gameSettings - Optional game settings object (for language configuration)
 * @param dialogs - Optional dialogs array for Database.Dialogs export
 */
export interface PackageResult {
  blob: Blob;
  filename: string;
  isZip: boolean;
}

export async function createCustomDataPackage(
  blueprintGroups: LibraryItemsByBlueprint[],
  gameId: string,
  gameName: string,
  exportTemplate: ExportTemplate,
  blueprints?: any[],
  types?: any[],
  tags?: any[],
  allItems?: any[],
  folderPathResolver?: (folderId: string | null | undefined) => string,
  namingConventionOverride?: NamingConvention,
  customNamespace?: string,
  customClassName?: string,
  gameSettings?: any,
  dialogs?: any[]
): Promise<PackageResult> {
  const zip = new JSZip();
  const filesToInclude: Array<{ name: string; content: string }> = [];
  
  // Check if this is a database export pattern
  const isDatabase = isDatabaseTemplate(exportTemplate);
  
  if (isDatabase) {
    // Database export: Create single database file
    
    // Unity export folder structure constants
    const DATA_FOLDER = 'data';
    const HELPERS_FOLDER = 'helpers';
    const ENGINE_FOLDER = 'GrimoireEngine';
    
    // Use allItems directly if available (they have the folder property)
    // Otherwise flatten from blueprint groups
    const items = allItems || blueprintGroups.flatMap(group => group.items.map(item => ({
      ...item,
      blueprint_id: group.blueprintId
    })));
    
    // Fetch translation strings for the game
    const stringsItems = getCachedStringsItems(gameId);
    
    // Get languages from game settings (used for translation key decisions)
    const gameLanguages = gameSettings?.languages || ['en'];
    const hasMultipleLanguages = gameLanguages.length > 1;
    
    let databaseContent: string;
    
    // Use folder-based export only if the template is folder-based AND a folder path resolver is provided
    // Otherwise use category-based export (for templates like preset-roblox-database)
    if (isFolderBasedTemplate(exportTemplate) && folderPathResolver) {
     
      databaseContent = renderFolderBasedDatabaseTemplate(
        exportTemplate,
        items,
        blueprints || [],
        folderPathResolver,
        types,
        tags,
        allItems,
        namingConventionOverride,
        customNamespace,
        customClassName,
        stringsItems,
        dialogs || [],
        undefined, // documents
        gameLanguages
      );
    } else {
      // Category-based export (default for preset-roblox-database and similar)
      databaseContent = renderDatabaseTemplate(
        exportTemplate,
        items,
        blueprints || [],
        types,
        tags,
        allItems
      );
    }
    
    // Add database file to ZIP - use custom class name for filename
    const className = customClassName || exportTemplate.databaseSettings?.className || 'Database';
    const fileExtension = exportTemplate.platform === 'unity' ? '.cs' 
      : exportTemplate.platform === 'json' ? '.json' 
      : '.lua';
    const filename = `${className}${fileExtension}`;
    zip.file(filename, databaseContent);
    filesToInclude.push({ name: filename, content: databaseContent });
    
    // Generate and add translation keys file (only for Unity C# exports with strings AND multiple languages)
    // When only one language is configured, translatable fields are exported as plain strings
    if (exportTemplate.platform === 'unity' && stringsItems.length > 0 && hasMultipleLanguages) {
      const namespace = customNamespace || exportTemplate.databaseSettings?.namespace || 'Grimoire.Data';
      const translationKeysClassName = exportTemplate.databaseSettings?.translationKeysClassName || 'TranslationKeys';
      const translationKeysFilename = exportTemplate.databaseSettings?.translationKeysFilename || 'TranslationKeys.cs';

      const translationKeysContent = renderTranslationKeysFile(stringsItems, namespace, translationKeysClassName, gameLanguages);
      const translationKeysPath = `${DATA_FOLDER}/${translationKeysFilename}`;
      zip.file(translationKeysPath, translationKeysContent);
      filesToInclude.push({ name: translationKeysPath, content: translationKeysContent });
    }
    
    // Generate and add Types file (for Unity C# exports)
    if (exportTemplate.platform === 'unity') {
      const namespace = customNamespace || exportTemplate.databaseSettings?.namespace || 'Grimoire.Data';
      const typesClassName = exportTemplate.databaseSettings?.typesClassName || 'Types';
      const typesFilename = exportTemplate.databaseSettings?.typesFilename || 'Types.cs';
      
      const typesContent = renderTypesFile(types || [], namespace, typesClassName);
      const typesPath = `${DATA_FOLDER}/${typesFilename}`;
      zip.file(typesPath, typesContent);
      filesToInclude.push({ name: typesPath, content: typesContent });
    }
    
    // Generate and add Tags file (for Unity C# exports)
    if (exportTemplate.platform === 'unity') {
      const namespace = customNamespace || exportTemplate.databaseSettings?.namespace || 'Grimoire.Data';
      const tagsClassName = exportTemplate.databaseSettings?.tagsClassName || 'Tags';
      const tagsFilename = exportTemplate.databaseSettings?.tagsFilename || 'Tags.cs';
      
      const tagsContent = renderTagsFile(tags || [], namespace, tagsClassName);
      const tagsPath = `${DATA_FOLDER}/${tagsFilename}`;
      zip.file(tagsPath, tagsContent);
      filesToInclude.push({ name: tagsPath, content: tagsContent });
    }
    
    // Generate and add Variables file (for Unity C# exports)
    // Variables are the same as "types" in the backend - they're stored in the types table
    // but represent game variables with numeric values, ranges, and linked references
    if (exportTemplate.platform === 'unity') {
      const namespace = customNamespace || exportTemplate.databaseSettings?.namespace || 'Grimoire.Data';
      const variablesClassName = exportTemplate.databaseSettings?.variablesClassName || 'Variables';
      const variablesFilename = exportTemplate.databaseSettings?.variablesFilename || 'Variables.cs';
      
      const variablesContent = renderVariablesFile(types || [], namespace, variablesClassName);
      const variablesPath = `${DATA_FOLDER}/${variablesFilename}`;
      zip.file(variablesPath, variablesContent);
      filesToInclude.push({ name: variablesPath, content: variablesContent });
      
      // Also generate runtime variables file for modifiable variables at runtime
      const runtimeVariablesClassName = exportTemplate.databaseSettings?.runtimeVariablesClassName || 'VariableRuntime';
      const runtimeVariablesFilename = exportTemplate.databaseSettings?.runtimeVariablesFilename || 'VariableRuntime.cs';
      
      const runtimeVariablesContent = renderRuntimeVariablesFile(types || [], namespace, runtimeVariablesClassName);
      const runtimeVariablesPath = `${ENGINE_FOLDER}/${runtimeVariablesFilename}`;
      zip.file(runtimeVariablesPath, runtimeVariablesContent);
      filesToInclude.push({ name: runtimeVariablesPath, content: runtimeVariablesContent });
    }
    
    // Generate and add DialogDatabase file (for Unity C# exports)
    if (exportTemplate.platform === 'unity') {
      const namespace = customNamespace || exportTemplate.databaseSettings?.namespace || 'Grimoire.Data';
      const dialogDatabaseClassName = exportTemplate.databaseSettings?.dialogDatabaseClassName || 'DialogDatabase';
      const dialogDatabaseFilename = exportTemplate.databaseSettings?.dialogDatabaseFilename || 'DialogDatabase.cs';
      
      
      // Create folder path resolver if not already provided
      const folderResolver = folderPathResolver || ((folderId: string | null | undefined) => '');
      
      // Build strings map for translation references
      // When single-language mode, keep empty so dialog text exports as plain strings
      const stringsMap = new Map<string, any>();
      if (hasMultipleLanguages) {
        stringsItems.forEach(item => {
          stringsMap.set(item.id, item);
        });
      }
      
      // Use the naming convention from the template
      const fieldConvention = exportTemplate.namingConventions.fieldName;
      
      const dialogDatabaseContent = renderDialogDatabaseFile(
        dialogs || [],
        folderResolver,
        stringsMap,
        fieldConvention,
        namespace,
        dialogDatabaseClassName
      );
      
      const dialogDatabasePath = `${DATA_FOLDER}/${dialogDatabaseFilename}`;
      zip.file(dialogDatabasePath, dialogDatabaseContent);
      filesToInclude.push({ name: dialogDatabasePath, content: dialogDatabaseContent });
    }
    
    // Generate and add DialogVariables file (for Unity C# exports)
    if (exportTemplate.platform === 'unity') {
      const namespace = customNamespace || exportTemplate.databaseSettings?.namespace || 'Grimoire.Data';
      const dialogVariablesClassName = exportTemplate.databaseSettings?.dialogVariablesClassName || 'DialogVariables';
      const dialogVariablesFilename = exportTemplate.databaseSettings?.dialogVariablesFilename || 'DialogVariables.cs';
      
      
      const dialogVariablesContent = renderDialogVariablesFile(
        dialogs || [],
        namespace,
        dialogVariablesClassName
      );
      
      const dialogVariablesPath = `${DATA_FOLDER}/${dialogVariablesFilename}`;
      zip.file(dialogVariablesPath, dialogVariablesContent);
      filesToInclude.push({ name: dialogVariablesPath, content: dialogVariablesContent });
    }
    
    // Generate JSON extra files (dialogs, translations, types, tags, variables)
    if (exportTemplate.platform === 'json') {
      const gameLanguages = gameSettings?.languages || ['en'];
      
      // Create folder path resolver if not already provided
      const folderResolver = folderPathResolver || ((folderId: string | null | undefined) => '');
      
      // Build strings map for translation references
      const jsonStringsMap = new Map<string, any>();
      stringsItems.forEach(item => {
        jsonStringsMap.set(item.id, item);
      });
      
      const fieldConvention = exportTemplate.namingConventions.fieldName;
      
      // Dialogs
      if (dialogs && dialogs.length > 0) {
        const dialogsJson = renderJsonDialogsFile(
          dialogs,
          folderResolver,
          jsonStringsMap,
          fieldConvention
        );
        zip.file('dialogs.json', dialogsJson);
        filesToInclude.push({ name: 'dialogs.json', content: dialogsJson });
      }
      
      // Translations
      if (stringsItems.length > 0) {
        const translationsJson = renderJsonTranslationsFile(stringsItems, gameLanguages);
        zip.file('translations.json', translationsJson);
        filesToInclude.push({ name: 'translations.json', content: translationsJson });
      }
      
      // Types
      if (types && types.length > 0) {
        const typesJson = renderJsonTypesFile(types);
        zip.file('types.json', typesJson);
        filesToInclude.push({ name: 'types.json', content: typesJson });
      }
      
      // Tags
      if (tags && tags.length > 0) {
        const tagsJson = renderJsonTagsFile(tags);
        zip.file('tags.json', tagsJson);
        filesToInclude.push({ name: 'tags.json', content: tagsJson });
      }
      
      // Variables (same data source as types)
      if (types && types.length > 0) {
        const variablesJson = renderJsonVariablesFile(types);
        zip.file('variables.json', variablesJson);
        filesToInclude.push({ name: 'variables.json', content: variablesJson });
      }
      
      // Dialog variables
      if (dialogs && dialogs.some((d: any) => d.data?.variables && d.data.variables.length > 0)) {
        const dialogVarsJson = renderJsonDialogVariablesFile(dialogs);
        zip.file('dialog_variables.json', dialogVarsJson);
        filesToInclude.push({ name: 'dialog_variables.json', content: dialogVarsJson });
      }
    }
    
    // Determine the game engine for proper naming (Unity vs Godot)
    const gameEngine = gameSettings?.engine?.toLowerCase() || 'unity';
    
    // Generate and add Runtime Layer files (only for Unity/Godot C# exports)
    // These provide engine-independent abstractions for logging, JSON, and storage
    if (exportTemplate.platform === 'unity') {
      const runtimeNamespace = customNamespace 
        ? `${customNamespace}.Runtime` 
        : 'Grimoire.Data.Runtime';
      // Pass the game engine to determine which bootstrap file to include
      const runtimeFiles: Record<string, string> = renderRuntimeLayerBundle(runtimeNamespace, gameEngine);
      
      // Map runtime files to their target folders
      const runtimeFileFolders: Record<string, string> = {
        'GrimoireJson.cs': HELPERS_FOLDER,
        'GrimoireLog.cs': HELPERS_FOLDER,
        'GrimoireStorage.cs': ENGINE_FOLDER,
        'UnityGrimoireBootstrap.cs': HELPERS_FOLDER,
        'GodotGrimoireBootstrap.cs': HELPERS_FOLDER
      };
      
      // Add each runtime file to the export in its designated folder
      for (const [runtimeFilename, content] of Object.entries(runtimeFiles)) {
        const folder = runtimeFileFolders[runtimeFilename] || '';
        const filePath = folder ? `${folder}/${runtimeFilename}` : runtimeFilename;
        zip.file(filePath, content);
        filesToInclude.push({ name: filePath, content });
      }
    }
    
    // Check if we only have one file - if so, return it directly without ZIP or README
    if (filesToInclude.length === 1) {
      const singleFile = filesToInclude[0];
      const mimeType = singleFile.name.endsWith('.json') ? 'application/json' : 'text/plain';
      const blob = new Blob([singleFile.content], { type: mimeType });
      return {
        blob,
        filename: singleFile.name,
        isZip: false
      };
    }
    
    // Add README
    const useFolderStructure = !!folderPathResolver;
    const namespaceName = customNamespace || exportTemplate.databaseSettings?.namespace || 'Grimoire.Data';
    const hasTranslations = exportTemplate.platform === 'unity' && stringsItems.length > 0;
    const hasDialogs = exportTemplate.platform === 'unity'; // Always generated for Unity
    const hasDialogVariables = hasDialogs && dialogs && dialogs.some((d: any) => d.data?.variables && d.data.variables.length > 0);
    const hasTypes = exportTemplate.platform === 'unity'; // Always generated for Unity
    const hasTags = exportTemplate.platform === 'unity'; // Always generated for Unity
    const hasVariables = exportTemplate.platform === 'unity'; // Always generated for Unity
    const hasRuntimeLayer = exportTemplate.platform === 'unity';
    const translationKeysFilename = exportTemplate.databaseSettings?.translationKeysFilename || 'TranslationKeys.cs';
    const dialogDatabaseFilename = exportTemplate.databaseSettings?.dialogDatabaseFilename || 'DialogDatabase.cs';
    const dialogVariablesFilename = exportTemplate.databaseSettings?.dialogVariablesFilename || 'DialogVariables.cs';
    const typesFilename = exportTemplate.databaseSettings?.typesFilename || 'Types.cs';
    const tagsFilename = exportTemplate.databaseSettings?.tagsFilename || 'Tags.cs';
    const variablesFilename = exportTemplate.databaseSettings?.variablesFilename || 'Variables.cs';
    
    // Determine the display name for the platform/engine
    const engineDisplayName = exportTemplate.platform === 'unity' 
      ? (gameEngine === 'godot' ? 'Godot C#' : 'Unity C#') 
      : exportTemplate.platform === 'json' 
        ? 'JSON'
        : 'Roblox Lua';
    
    const readmeContent = `${exportTemplate.name}
${'='.repeat(50)}

This package contains ${hasTranslations || hasDialogs || hasVariables || hasTags || hasTypes ? 'database and additional files' : 'a single database export file'} for ${engineDisplayName}.

Folder Structure:
- ${filename} (main database)
${hasTranslations || hasTypes || hasTags || hasVariables || hasDialogs || hasDialogVariables ? `
${DATA_FOLDER}/
${hasTranslations ? `  - ${translationKeysFilename} (translations)` : ''}
${hasTypes ? `  - ${typesFilename} (type definitions)` : ''}
${hasTags ? `  - ${tagsFilename} (tag definitions)` : ''}
${hasVariables ? `  - ${variablesFilename} (game variables with type-safe references)` : ''}
${hasDialogs ? `  - ${dialogDatabaseFilename} (dialog sections dictionary)` : ''}
${hasDialogVariables ? `  - ${dialogVariablesFilename} (dialog variables)` : ''}` : ''}
${hasRuntimeLayer ? `
${HELPERS_FOLDER}/ (engine-independent abstractions)
  - GrimoireLog.cs (logging abstraction)
  - GrimoireJson.cs (JSON serialization abstraction)
${gameEngine === 'godot' 
  ? '  - GodotGrimoireBootstrap.cs (Godot adapter - call Initialize() in _Ready())' 
  : '  - UnityGrimoireBootstrap.cs (Unity adapter - auto-configures at startup)'}

${ENGINE_FOLDER}/ (runtime engine)
  - GrimoireStorage.cs (persistent storage abstraction)
${hasVariables ? '  - VariableRuntime.cs (runtime variable management)' : ''}` : ''}

Platform: ${exportTemplate.platform.toUpperCase()}
Template: ${exportTemplate.name}
Structure: ${useFolderStructure ? 'Folder-based hierarchy' : 'Category-based'}
${exportTemplate.platform === 'unity' ? `Namespace: ${namespaceName}` : ''}
Class/Table Name: ${className}

${useFolderStructure ? `
The database file organizes your library objects by their folder structure.
Access data using dot notation based on folder paths:
  ${exportTemplate.platform === 'unity' ? `${className}.scenes.player_house.bed.inspect_text` : `${className}.scenes.player_house.bed.inspect_text`}
` : `
The database file contains all your library objects organized by category.
`}

${hasTranslations ? `
Translation Support:
- Translatable text fields use TranslationKey objects that automatically return the correct language
- Set the current language: TranslationKey.CurrentLanguage = "en"; (default is "en")
- Access translations automatically: string text = item.FieldName; (uses current language)
- Access specific language: string text = item.FieldName["nl"]; (gets Dutch translation)
- Arrays work the same: string text = item.FieldArray[0]; (gets first element in current language)
- Call explicitly: string text = item.FieldName.GetText(); (uses current language)
- TranslationKey automatically converts to string when needed

Example usage:
  // Set language once at game start
  TranslationKey.CurrentLanguage = "nl";
  
  // Get text automatically uses current language (Dutch)
  string bedText = Database.Library["scenes/player_house/bed"].inspect_text[0];
  
  // Or get specific language
  string englishText = Database.Library["scenes/player_house/bed"].inspect_text[0]["en"];
` : ''}

${hasDialogs ? `
Dialog System:
- Dialog sections are stored in ${dialogDatabaseFilename} with dictionary-based access
- Access dialogs using: Database.Get<DialogSectionType>("dialogKey/sectionId")
- Navigate through dialogs: DialogDatabase.GetNext(currentSection)
- Each section contains fields with text, type, speaker, and navigation info
- Question fields have options with their own navigation

Example dialog usage:
  // Get a specific dialog section
  var section = Database.Get<QuestDialog_WelcomeSection>("quest_dialog/welcome");
  
  // Access the dialog text (uses current language if translatable)
  string dialogText = section.field_0_dialog_text;
  
  // Check navigation
  string nextSectionId = section.field_0_next_section;
  
  // Navigate to next section automatically
  var nextSection = DialogDatabase.GetNext(section);
  
  // For questions with options, handle each option:
  var option1Text = section.field_1_question_options.option_0;
  string option1NextSection = section.field_1_question_options.option_0_next_section;
` : ''}

${hasVariables ? `
Game Variables (${variablesFilename}):
- Variables represent game data that can have different types and values
- Supports 5 variable types: String, Number, Percentage, Range, and Type
- Variables can be linked to other variables for type-safe value references
- Each variable type has specialized methods for accessing values

Variable Types:
1. String Variables: Simple string values
   - Access: Variables.ItemType.Sword.Name

2. Number Variables: Numeric values with optional linked references
   - Access value: Variables.Damage.Light.GetValue()
   - Direct value: Variables.Damage.Light.NumericValue

3. Percentage Variables: Values representing percentages (0-100)
   - Access percentage: Variables.DropChance.Rare.GetValue()
   - Get as decimal (0.0-1.0): Variables.DropChance.Rare.GetNormalizedValue()

4. Range Variables: Min/max/increment values with optional linked references
   - Access min: Variables.Level.Easy.GetMin()
   - Access max: Variables.Level.Easy.GetMax()
   - Access step: Variables.Level.Easy.GetIncrement()

5. Type Variables: References to other variables (type-safe object linking)
   - Get linked value: Variables.WeaponClass.Warrior.GetLinkedValue()

Example usage:
  // String variable
  string swordName = Variables.ItemType.Sword.Name;
  
  // Number variable with linked value
  double damageValue = Variables.Damage.Heavy.GetValue();
  
  // Percentage variable
  double dropRate = Variables.DropChance.Rare.GetNormalizedValue(); // 0.0-1.0
  
  // Range variable
  double minLevel = Variables.PlayerLevel.Beginner.GetMin();
  double maxLevel = Variables.PlayerLevel.Beginner.GetMax();
  double levelStep = Variables.PlayerLevel.Beginner.GetIncrement();
  
  // Type variable (linked reference)
  var linkedItem = Variables.WeaponClass.Warrior.GetLinkedValue();
  
  // Linked values automatically resolve to their source
  // If Variable A is linked to Variable B, calling A.GetValue() returns B.GetValue()
` : ''}

${hasDialogVariables ? `
Dialog Variables:
- Dialog variables are stored in ${dialogVariablesFilename}
- Variables can be boolean or number types
- Each variable has an initial value that can be modified at runtime
- Variables are grouped by dialog for organization

Example dialog variable usage:
  // Access a boolean variable
  bool hasKey = DialogVariables.quest_dialog.has_key;
  
  // Set a boolean variable
  DialogVariables.quest_dialog.has_key.BoolValue = true;
  
  // Access a number variable
  float questProgress = DialogVariables.quest_dialog.progress;
  
  // Increment a number variable
  DialogVariables.quest_dialog.progress.NumberValue += 10f;
  
  // Reset to initial value
  DialogVariables.quest_dialog.progress.Reset(0f);
  
  // Check variable type
  string varType = DialogVariables.quest_dialog.progress.Type; // "number" or "boolean"
` : ''}

${exportTemplate.platform === 'json' ? `
FILES OVERVIEW
==============
- ${filename}             Main database with all library objects
- dialogs.json            Dialog trees with nodes, conditions, branching, and navigation
- translations.json       All translation strings with per-language text
- types.json              Type/enum definitions with elements
- tags.json               Tag group definitions with elements
- variables.json          Game variable definitions with values
- dialog_variables.json   Per-dialog variable declarations with initial values

(Files are only included when they contain data.)


DATABASE (${filename})
${'='.repeat(20)}
${useFolderStructure ? `Items are organized by folder hierarchy as nested objects.

  {
    "folder_name": {
      "subfolder": {
        "item_name": {
          "field_name": "value",
          "numeric_field": 42,
          "tag_field": ["tag1", "tag2"]
        }
      }
    }
  }

Access: data.folder_name.subfolder.item_name.field_name` : `Items are grouped by blueprint/category name.

  {
    "category_name": {
      "item_key": {
        "field_name": "value",
        "numeric_field": 42,
        "tag_field": ["tag1", "tag2"]
      }
    }
  }

Access: data["category_name"]["item_key"]["field_name"]`}


DIALOG SYSTEM (dialogs.json)
=============================
The dialog system is a node-based graph. Each dialog contains an array of nodes.
You traverse the graph by following navigation references from node to node.

Top-level structure:
  {
    "dialogs": {
      "dialog_key": {
        "id": "uuid",
        "name": "Quest Dialog",
        "dialog_key": "quest_dialog",
        "starting_node": "greeting",       // node_identifier of the entry point
        "variables": [ ... ],              // variables scoped to this dialog
        "nodes": [ ... ]                   // all nodes in this dialog
      }
    }
  }

NODE TYPES
----------
Each node has a "type" field. The types are:

1. "dialog"       - Text spoken by a character. Has text, speaker, next_node.
2. "question"     - Player choice. Has text, options[] with navigation per option.
3. "condition"    - Branching based on variable checks. Has condition, condition_options[].
4. "setter"       - Modifies a variable. Has setter { variable, operator, value }, next_node.
5. "end-dialog"   - Ends the dialog.
6. "context"      - Internal note/context (not shown to player). Has context text.

NAVIGATION
----------
Every node can navigate to another node using these fields:
- "next_node": "node_identifier"              → go to node in SAME dialog
- "next_dialog": "other_dialog_key"           → jump to a DIFFERENT dialog
- "next_dialog_node": "node_identifier"       → target node in that other dialog

To traverse: start at "starting_node", find the node with that node_identifier,
read its content, then follow its next_node (or let the user pick an option).

DIALOG NODE EXAMPLE
  {
    "id": "abc-123",
    "type": "dialog",
    "node_identifier": "greeting",
    "order_index": 0,
    "text": { "en": "Hello traveler!", "nl": "Hallo reiziger!" },
    "speaker": "innkeeper",
    "next_node": "ask_quest"
  }

  Text can be a string (plain text) or an object with language keys (translated).

QUESTION NODE EXAMPLE
  {
    "type": "question",
    "node_identifier": "ask_quest",
    "text": { "en": "Do you want to help me?", "nl": "Wil je me helpen?" },
    "options": [
      {
        "text": { "en": "Yes, I will help!", "nl": "Ja, ik help!" },
        "next_node": "accept_quest",
        "variable_action": {                   // optional: modify variable on pick
          "variable": "quest_accepted",
          "operator": "set",
          "value": true
        }
      },
      {
        "text": { "en": "Not now.", "nl": "Nu niet." },
        "next_node": "decline_quest",
        "visibility_condition": {              // optional: only show if condition met
          "variable": "player_level",
          "operator": ">=",
          "value": 5
        }
      }
    ]
  }

CONDITION NODE EXAMPLE
  A condition node checks a variable and branches to different nodes.

  {
    "type": "condition",
    "node_identifier": "check_score",
    "condition": {
      "variable": "player_score"               // which variable to evaluate
    },
    "condition_options": [
      {
        "operator": ">",                       // comparison: ==, !=, >, <, >=, <=, in_range
        "value": 100,                          // value to compare against
        "next_node": "high_score_path"         // where to go if this condition matches
      },
      {
        "operator": "<=",
        "value": 100,
        "next_node": "low_score_path"
      },
      {
        "operator": "in_range",                // special: checks if value falls in a range
        "value": "range_element_id",           // references a range definition
        "next_dialog": "special_dialog",       // can also jump to another dialog
        "next_dialog_node": "start"
      }
    ]
  }

  Operators: == (equals), != (not equals), > (greater than), < (less than),
             >= (greater or equal), <= (less or equal), in_range (within a range),
             contains, startsWith, endsWith (for text variables)

  Evaluation: iterate condition_options in order, the FIRST match determines
  which next_node/next_dialog to follow.

SETTER NODE EXAMPLE
  A setter node modifies a variable and then continues to the next node.

  {
    "type": "setter",
    "node_identifier": "add_gold",
    "setter": {
      "variable": "gold",
      "operator": "increment",                 // set, increment, or decrement
      "value": 50
    },
    "next_node": "after_reward"
  }

DIALOG VARIABLES
  Each dialog can declare variables scoped to that dialog run:

  "variables": [
    { "name": "quest_accepted", "type": "boolean", "initial_value": false },
    { "name": "player_score", "type": "number", "initial_value": 0 },
    { "name": "player_name", "type": "text", "initial_value": "" },
    {
      "name": "difficulty",
      "type": "range",
      "initial_value": 5,
      "range_min": 1,
      "range_max": 10,
      "increment": 1
    }
  ]

  Variable types: boolean, number, text, range, range-selector


TRANSLATIONS (translations.json)
=================================
  {
    "languages": ["en", "nl", "fr"],
    "translations": {
      "LOCA_KEY": {
        "id": "uuid",
        "source_text": "Hello",
        "translations": {
          "en": "Hello",
          "nl": "Hallo",
          "fr": "Bonjour"
        }
      }
    }
  }

  In dialogs and database fields, translatable text appears as:
  { "en": "Hello", "nl": "Hallo" }  (object with language code keys)
  Non-translatable text appears as a plain string: "Hello"


TYPES (types.json)
==================
  {
    "types": {
      "GroupName": {
        "id": "uuid",
        "elements": [
          { "id": "uuid", "name": "ElementName", "code": "element_code", "value": "" }
        ]
      }
    }
  }


TAGS (tags.json)
================
  {
    "tags": {
      "GroupName": {
        "id": "uuid",
        "elements": [
          { "id": "uuid", "name": "TagName", "code": "tag_code", "value": "" }
        ]
      }
    }
  }


VARIABLES (variables.json)
==========================
  Game-level variables (not dialog-scoped):
  {
    "variables": {
      "GroupName": {
        "id": "uuid",
        "elements": [
          { "id": "uuid", "name": "ElementName", "code": "code", "value": "..." }
        ]
      }
    }
  }


DIALOG VARIABLES (dialog_variables.json)
=========================================
  Per-dialog variable declarations:
  {
    "dialog_variables": {
      "dialog_key": {
        "dialog_name": "Quest Dialog",
        "variables": [
          { "name": "has_key", "type": "boolean", "initial_value": false },
          { "name": "progress", "type": "number", "initial_value": 0 }
        ]
      }
    }
  }


QUICK START (JavaScript/TypeScript)
====================================
  // Load files
  const database = JSON.parse(fs.readFileSync('${filename}', 'utf-8'));
  const dialogs = JSON.parse(fs.readFileSync('dialogs.json', 'utf-8'));
  const translations = JSON.parse(fs.readFileSync('translations.json', 'utf-8'));

  // Access library item
  ${useFolderStructure ? `const item = database.folder_name.item_name;` : `const item = database["category"]["item_key"];`}
  const fieldValue = item.field_name;

  // Run a dialog
  const dialog = dialogs.dialogs["quest_dialog"];
  let currentNodeId = dialog.starting_node;

  while (currentNodeId) {
    const node = dialog.nodes.find(n => n.node_identifier === currentNodeId);
    if (!node) break;

    if (node.type === "dialog") {
      const text = typeof node.text === "string" ? node.text : node.text["en"];
      console.log(node.speaker + ": " + text);
      currentNodeId = node.next_node;
    } else if (node.type === "question") {
      // Show options, let user pick, follow option.next_node
      currentNodeId = node.options[0].next_node; // simplified
    } else if (node.type === "condition") {
      // Evaluate condition_options, follow matching branch
      const varValue = getVariable(node.condition.variable);
      for (const opt of node.condition_options) {
        if (evaluateCondition(varValue, opt.operator, opt.value)) {
          currentNodeId = opt.next_node;
          break;
        }
      }
    } else if (node.type === "setter") {
      // Modify variable, then continue
      applyAction(node.setter.variable, node.setter.operator, node.setter.value);
      currentNodeId = node.next_node;
    } else if (node.type === "end-dialog") {
      break;
    }
  }
` : exportTemplate.platform === 'unity' ? (gameEngine === 'godot' ? `
To use in Godot:
1. Extract and place the entire folder structure in your Godot C# project's scripts folder
   (keep the ${DATA_FOLDER}/, ${HELPERS_FOLDER}/, and ${ENGINE_FOLDER}/ folders as-is)
2. Add "using ${namespaceName};" at the top of your script
3. Call GodotGrimoireBootstrap.Initialize() in your main script's _Ready() method
4. ${hasTranslations ? 'Set the language (once at game start): TranslationKey.CurrentLanguage = "en";' : ''}
5. Access the data: ${useFolderStructure ? `${className}.FolderName.SubFolder.ItemName.FieldName` : `${className}.Category.Data["ITEM_ID"]["FieldName"]`}
${hasVariables ? `6. Access game variables: Variables.VariableName.ElementName.GetValue()` : ''}
${hasTranslations ? `${hasVariables ? '7' : '6'}. Translatable fields automatically return text in the current language` : ''}
` : `
To use in Unity:
1. Extract and place the entire folder structure in your Assets/Scripts/Database folder
   (keep the ${DATA_FOLDER}/, ${HELPERS_FOLDER}/, and ${ENGINE_FOLDER}/ folders as-is)
2. Add "using ${namespaceName};" at the top of your script
3. ${hasTranslations ? 'Set the language (once at game start): TranslationKey.CurrentLanguage = "en";' : ''}
4. Access the data: ${useFolderStructure ? `${className}.FolderName.SubFolder.ItemName.FieldName` : `${className}.Category.Data["ITEM_ID"]["FieldName"]`}
${hasVariables ? `5. Access game variables: Variables.VariableName.ElementName.GetValue()` : ''}
${hasTranslations ? `${hasVariables ? '6' : '5'}. Translatable fields automatically return text in the current language` : ''}
`) : `
To use in Roblox:
1. Place ${filename} in your ReplicatedStorage folder
2. Require it: local ${className} = require(game.ReplicatedStorage.${className})
3. Access the data: ${useFolderStructure ? `${className}.FolderName.SubFolder.ItemName.FieldName` : `${className}.Category.ITEM_ID.FieldName`}
`}

Generated by LocaNest - https://locanest.app
`;
    zip.file('README.txt', readmeContent);
    
  } else {
    // Regular code generation export
    console.log('📦 Creating code package export for', exportTemplate.platform);
    
    // Add manifest
    // Map platform to manifest platform (manifest only supports cpp/roblox)
    const manifestPlatform = exportTemplate.platform === 'cpp' ? 'cpp' : 'roblox';
    const manifestJson = generateManifestJson(
      blueprintGroups, 
      gameId, 
      gameName, 
      manifestPlatform as 'cpp' | 'roblox'
    );
    zip.file('manifest.json', manifestJson);
    
    // Track created folders
    const createdFolders = new Set<string>();
    
    // Helper to ensure folder exists
    const ensureFolder = (folderPath: string) => {
      if (!createdFolders.has(folderPath)) {
        zip.folder(folderPath);
        createdFolders.add(folderPath);
      }
    };
    
    // Add data folder with JSON files
    ensureFolder('data');
    blueprintGroups.forEach(group => {
      group.items.forEach(item => {
        const dataFileName = sanitizeFilename(item.name) + '.json';
        const dataJson = generateItemDataJson(item);
        zip.file(`data/${dataFileName}`, dataJson);
      });
    });
    
    // Process each blueprint group with the custom template
    blueprintGroups.forEach(group => {
      const files = generateCustomCodeForBlueprint(group, exportTemplate);
      
      // Add generated files to appropriate folders
      files.forEach((fileData, filename) => {
        const { content, location } = fileData;
        
        // Ensure the folder exists
        if (location) {
          ensureFolder(location);
          zip.file(`${location}/${filename}`, content);
        } else {
          zip.file(filename, content);
        }
      });
    });
    
    // Add README files
    const typeLocation = exportTemplate.typeFile.location || 'types';
    const instanceLocation = exportTemplate.instanceFile.location || 'instances';
    
    if (exportTemplate.typeFile.enabled) {
      ensureFolder(typeLocation);
      zip.file(`${typeLocation}/README.txt`, generateReadmeForLocation(typeLocation, exportTemplate));
    }
    
    if (exportTemplate.instanceFile.enabled) {
      ensureFolder(instanceLocation);
      zip.file(`${instanceLocation}/README.txt`, generateReadmeForLocation(instanceLocation, exportTemplate));
    }
  }
  
  // Generate ZIP
  const zipBlob = await zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 }
  });
  
  // Generate filename for the ZIP
  const cleanGameName = sanitizeFilename(gameName);
  const cleanTemplateName = sanitizeFilename(exportTemplate.name);
  const timestamp = new Date().toISOString().split('T')[0].replace(/-/g, '');
  const zipFilename = `${cleanGameName}_${cleanTemplateName}_${timestamp}.zip`;
  
  return {
    blob: zipBlob,
    filename: zipFilename,
    isZip: true
  };
}

/**
 * Generate README content for a specific location
 */
function generateReadmeForLocation(location: string, template: ExportTemplate): string {
  const platform = template.platform.toUpperCase();
  
  return `${template.name} - ${location} Files
${'='.repeat(50)}

Platform: ${platform}
Template: ${template.name}
${template.description ? `\nDescription: ${template.description}\n` : ''}

Location: ${location}/
${'='.repeat(50)}

These files were generated using the "${template.name}" export template.
${template.isPreset ? 'This is a preset template provided by LocaNest.' : 'This is a custom template.'}

File Naming Convention: ${template.namingConventions.fileName}
Field Naming Convention: ${template.namingConventions.fieldName}

${location === template.typeFile.location ? `
TYPE DEFINITION FILES
---------------------
These files contain type definitions and data structures for your templates.
${template.isPreset && template.platform === 'roblox' ? `
To use in Roblox:
1. Place these files in your ReplicatedStorage/Types folder
2. Require them in your scripts:
   local QuestType = require(game.ReplicatedStorage.Types.Quest)
` : ''}
` : ''}

${location === template.instanceFile.location ? `
INSTANCE FILES
--------------
These files contain actual data instances created from your library objects.
${template.isPreset && template.platform === 'roblox' ? `
To use in Roblox:
1. Place these files in your appropriate game folder
2. Require them to load the data:
   local myQuest = require(game.ReplicatedStorage.Quests.quest_meet_mia)
` : ''}
` : ''}

For more information about this export template, visit the Export Templates
section in Settings → Data Manager → Export Templates.

Generated by LocaNest - https://locanest.app
`;
}

/**
 * Download a ZIP blob with a specific filename
 */
export function downloadZipBlob(blob: Blob, filename: string): void {
  try {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to download ZIP:', error);
    throw new Error('Failed to download package');
  }
}

/**
 * Generate filename for custom data package
 */
export function generateCustomPackageFilename(
  gameName: string,
  templateName: string
): string {
  const cleanGameName = sanitizeFilename(gameName);
  const cleanTemplateName = sanitizeFilename(templateName);
  const timestamp = new Date().toISOString().split('T')[0].replace(/-/g, '');
  return `${cleanGameName}_${cleanTemplateName}_${timestamp}.zip`;
}

